import { useCallback } from "react";
import JSZip from "jszip";
import { saveAs } from "file-saver";
import { jsPDF } from "jspdf";
import { staticReportStore } from "../../../../../stores/stores";

const globalChartRefs: { [tabKey: string]: { [chartKey: string]: { getChart: () => any } } } = {};

const useChartExport = () => {
  const createChartRef = useCallback((tabKey: string, chartKey: string) => (ref: any) => {
    if (ref && ref.getChart) {
      if (!globalChartRefs[tabKey]) {
        globalChartRefs[tabKey] = {};
      }
      globalChartRefs[tabKey][chartKey] = ref;
    }
  }, []);

  const chartToDataUrl = (chart: any): Promise<string> => {
    return new Promise((resolve, reject) => {
      try {
        const svg = chart.getSVG({
          chart: { width: 1200, height: 800 }
        });
        
        const canvas = document.createElement("canvas");
        canvas.width = 1200;
        canvas.height = 800;
        const ctx = canvas.getContext("2d");
        
        const img = new Image();
        const svgBase64 = btoa(unescape(encodeURIComponent(svg)));
        const dataUrl = `data:image/svg+xml;base64,${svgBase64}`;
        
        img.onload = () => {
          if (ctx) {
            ctx.fillStyle = "#ffffff";
            ctx.fillRect(0, 0, canvas.width, canvas.height);
            ctx.drawImage(img, 0, 0);
          }
          resolve(canvas.toDataURL("image/png", 1.0));
        };
        
        img.onerror = () => reject(new Error("Failed to load SVG"));
        img.src = dataUrl;
      } catch (error) {
        reject(error);
      }
    });
  };

  const dataUrlToBlob = (dataUrl: string): Blob => {
    const arr = dataUrl.split(",");
    const mime = arr[0].match(/:(.*?);/)?.[1] || "image/png";
    const bstr = atob(arr[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);
    while (n--) {
      u8arr[n] = bstr.charCodeAt(n);
    }
    return new Blob([u8arr], { type: mime });
  };

  const pngToPdfBlob = (pngDataUrl: string, filename: string): Blob => {
    const pdf = new jsPDF({
      orientation: "landscape",
      unit: "px",
      format: [1200, 800],
    });
    
    pdf.addImage(pngDataUrl, "PNG", 0, 0, 1200, 800);
    return pdf.output("blob");
  };

  const handleExportImage = useCallback((
    tabKey: string,
    chartKey: string,
    format: string
  ) => {
    const chartRef = globalChartRefs[tabKey]?.[chartKey];
    const chart = chartRef?.getChart?.();
    
    if (chart) {
      if (chart.exportChartLocal) {
        chart.exportChartLocal({
          type: format,
          filename: chartKey,
        });
      }
    } else {
      console.warn(`No chart found for tab: ${tabKey}, key: ${chartKey}`);
    }
  }, []);

  const handleExportAllImagesAsZip = useCallback(async (tabKey: string) => {
    const { exportType } = staticReportStore.getState();
    const zip = new JSZip();

    const tabCharts = globalChartRefs[tabKey] || {};
    const chartKeys = Object.keys(tabCharts);

    console.log("Export - Tab:", tabKey, "Charts found:", chartKeys, "Type:", exportType);

    if (chartKeys.length === 0) {
      console.warn(`Không có biểu đồ nào được đăng ký cho tab ${tabKey}.`);
      return;
    }

    let exportedCount = 0;
    const extension = exportType === "pdf" ? "pdf" : "png";

    for (const chartKey of chartKeys) {
      const chartRef = tabCharts[chartKey];
      const chart = chartRef?.getChart?.();

      if (chart && chart.getSVG) {
        try {
          const pngDataUrl = await chartToDataUrl(chart);
          
          if (exportType === "pdf") {
            const pdfBlob = pngToPdfBlob(pngDataUrl, chartKey);
            zip.file(`${chartKey}.pdf`, pdfBlob);
          } else {
            const pngBlob = dataUrlToBlob(pngDataUrl);
            zip.file(`${chartKey}.png`, pngBlob);
          }
          
          exportedCount++;
          console.log(`Exported: ${chartKey}`);
        } catch (error) {
          console.error(`Failed to export chart: ${chartKey}`, error);
        }
      } else {
        console.warn(`Chart not ready: ${chartKey}`);
      }
    }

    if (exportedCount > 0) {
      const content = await zip.generateAsync({ type: "blob" });
      const tabName = tabKey === "1" ? "tai-nguyen" : tabKey === "2" ? "phan-hoi-danh-gia" : "hanh-vi-nguoi-dung";
      saveAs(content, `bieu-do-${tabName}.zip`);
      console.log(`Downloaded ${exportedCount} ${extension.toUpperCase()} files as ZIP`);
    } else {
      console.warn("Không có biểu đồ nào được export thành công");
    }
  }, []);

  return {
    createChartRef,
    handleExportImage,
    handleExportAllImagesAsZip,
  };
};

export default useChartExport;
